import { useEffect, useState } from "react";
import { adminContacts, adminMarkContact, adminDeleteContact } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Building2, Trash2, Check } from "lucide-react";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminContacts()
      .then((data) => {
        setItems(data);
        if (data[0]) setActive(data[0]);
      })
      .catch(() => toast.error("Couldn't load inbox."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (item, read) => {
    try {
      await adminMarkContact(item.id, read);
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, read } : x)));
      if (active?.id === item.id) setActive({ ...active, read });
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this brief?")) return;
    try {
      await adminDeleteContact(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (active?.id === item.id) setActive(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-10">
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
        Briefs
      </div>
      <h1 className="font-display text-4xl tracking-tight">Inbox</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 xl:col-span-4 bg-[#0f0f0f] border border-[#1a1a1a]">
          {loading ? (
            <div className="text-[#737373] text-xs tracking-[0.24em] uppercase p-6">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-[#737373] text-xs tracking-[0.24em] uppercase p-6">No briefs yet.</div>
          ) : (
            <ul className="divide-y divide-[#1a1a1a]" data-testid="inbox-list">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => { setActive(c); if (!c.read) markRead(c, true); }}
                    data-testid={`inbox-item-${c.id}`}
                    className={`w-full text-left p-4 flex flex-col gap-1 hover:bg-[#141414] ${active?.id === c.id ? "bg-[#141414]" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display text-sm tracking-tight">
                        {c.name}
                      </div>
                      {!c.read && (
                        <span className="text-[9px] tracking-[0.22em] uppercase bg-[#4A6D8C] text-white px-2 py-0.5">
                          New
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#A3A3A3] truncate">{c.company || c.email}</div>
                    <div className="text-[11px] text-[#525252] truncate">{c.message}</div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#525252] mt-1">
                      {fmt(c.submitted_at)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 xl:col-span-8 bg-[#0f0f0f] border border-[#1a1a1a] p-6 lg:p-8 min-h-[300px]">
          {!active ? (
            <div className="text-[#737373] text-xs tracking-[0.24em] uppercase">
              Select a brief to read.
            </div>
          ) : (
            <div data-testid="inbox-detail">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#737373] mb-2">
                    Brief from
                  </div>
                  <div className="font-display text-2xl tracking-tight">{active.name}</div>
                  <div className="text-[13px] text-[#A3A3A3] mt-1 flex items-center gap-4 flex-wrap">
                    {active.company && <span className="flex items-center gap-2"><Building2 size={12} /> {active.company}</span>}
                    <a href={`mailto:${active.email}`} className="flex items-center gap-2 hover:text-white"><Mail size={12} /> {active.email}</a>
                    {active.phone && <span className="flex items-center gap-2"><Phone size={12} /> {active.phone}</span>}
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#525252] mt-2">
                    {fmt(active.submitted_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => markRead(active, !active.read)}
                    data-testid="inbox-mark-read"
                    className="hl-btn-ghost text-[11px] inline-flex items-center gap-2"
                  >
                    <Check size={12} /> Mark {active.read ? "unread" : "read"}
                  </button>
                  <button
                    onClick={() => remove(active)}
                    data-testid="inbox-delete"
                    className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 hover:!border-red-400 hover:!text-red-200"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>

              {active.interested_in?.length > 0 && (
                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-2">Interested in</div>
                  <div className="flex flex-wrap gap-2">
                    {active.interested_in.map((i) => (
                      <span key={i} className="text-[10px] tracking-[0.18em] uppercase border border-[#262626] px-2 py-1">{i}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-2">Message</div>
                <div className="text-[14px] text-[#c9d1d9] leading-relaxed whitespace-pre-wrap">
                  {active.message}
                </div>
              </div>

              <a
                href={`mailto:${active.email}?subject=Re%3A%20HighwaysLens%20Brief&body=Hi%20${encodeURIComponent(active.name.split(" ")[0] || "")}%2C%0A%0A`}
                data-testid="inbox-reply"
                className="hl-btn-primary mt-8 inline-flex"
              >
                Reply via email
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
